import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { endpoint } from "../../api/endpoint";

const MAX_CSV_FILE_SIZE = 80 * 1024 * 1024; // 80MB limit
const MAX_CSV_ROWS = 250; // Maximum 250 rows allowed at a time

const TITLE_ALLOWED_REGEX = /^[a-zA-Z0-9\s\-_\(\)]*$/;
const CONTAINS_LETTER_REGEX = /[a-zA-Z]/;
const SKU_ALLOWED_REGEX = /^[a-zA-Z0-9\-_]*$/;
const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;

const parseCSVLine = (line) => {
  const result = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === "," && !inQuotes) {
      result.push(cur.trim());
      cur = "";
    } else {
      cur += c;
    }
  }
  result.push(cur.trim());
  return result;
};

const normalizeGender = (val) => {
  if (!val) return "";
  const s = String(val).trim().toLowerCase();
  if (s === "female" || s === "women" || s === "woman") return "women";
  if (s === "male" || s === "men" || s === "man") return "men";
  return s;
};

const normalizeMode = (val) => {
  if (!val) return "";
  const s = String(val).trim().toLowerCase().replace(/\s+/g, "_");
  if (s === "mannequin" || s === "mannequin_to_model") return "mannequin_to_model";
  if (s === "model" || s === "model_to_model") return "model_to_model";
  return s;
};

export const useBulkProductUploadModal = ({ isOpen, onClose, onSubmit, onRefreshProducts }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setSelectedFile(null);
    setIsDragOver(false);
    setParsedData([]);
    setValidationErrors([]);
    setUploadResult(null);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const parseAndValidateCSV = (fileText) => {
    const rawLines = fileText.split(/\r?\n/);
    const lines = rawLines.map((l) => l.trim()).filter((l) => l.length > 0);

    if (lines.length === 0) {
      return {
        validData: [],
        errors: [{ row: 0, errors: ["CSV file is empty"] }],
      };
    }

    const dataRowsCount = lines.length - 1;
    if (dataRowsCount > MAX_CSV_ROWS) {
      return {
        validData: [],
        errors: [
          {
            row: 0,
            sku: "ROW_LIMIT",
            errors: [
              `CSV exceeds maximum limit of 250 rows per batch (found ${dataRowsCount} rows). Please upload up to 250 products at a time.`,
            ],
          },
        ],
      };
    }

    const rawHeaders = parseCSVLine(lines[0]);
    const normalizedHeaders = rawHeaders.map((h) => h.trim().toLowerCase());

    const REQUIRED_COLUMNS = [
      { name: "SKU", match: ["sku"] },
      { name: "Title", match: ["title"] },
      { name: "Price", match: ["price"] },
      { name: "Gender", match: ["gender"] },
      { name: "Mode", match: ["mode"] },
      { name: "Filter Categories", match: ["filter categories", "filtercategories"] },
      { name: "Collection Category", match: ["collection category", "collectioncategory"] },
    ];

    const missingCols = REQUIRED_COLUMNS.filter(
      (col) => !col.match.some((alias) => normalizedHeaders.includes(alias))
    ).map((col) => col.name);

    if (missingCols.length > 0) {
      return {
        validData: [],
        errors: [
          {
            row: 1,
            sku: "HEADER",
            errors: [
              `Invalid CSV header columns. Missing required column(s): ${missingCols.join(", ")}. Columns must match sample CSV: SKU, Title, Price, Gender, Mode, Filter Categories, Collection Category, Description.`,
            ],
          },
        ],
      };
    }

    const headers = normalizedHeaders.map((h) => h.replace(/[\s_-]+/g, ""));
    const getColIndex = (aliases) => {
      return headers.findIndex((h) => aliases.includes(h));
    };

    const skuIdx = getColIndex(["sku"]);
    const titleIdx = getColIndex(["title"]);
    const priceIdx = getColIndex(["price"]);
    const genderIdx = getColIndex(["gender"]);
    const modeIdx = getColIndex(["mode"]);
    const filterCatIdx = getColIndex(["filtercategories"]);
    const collectionCatIdx = getColIndex(["collectioncategory"]);
    const descIdx = getColIndex(["description", "desc"]);

    const validData = [];
    const errorsList = [];
    const seenSkus = new Map();

    for (let i = 1; i < lines.length; i++) {
      const rowNum = i + 1; // 1-based index in file (Row 2 = first data row)
      const cells = parseCSVLine(lines[i]);

      // Check if entire row is empty
      if (cells.every((c) => !c)) {
        continue;
      }

      const rawSku = skuIdx >= 0 ? (cells[skuIdx] || "").trim() : "";
      const rawTitle = titleIdx >= 0 ? (cells[titleIdx] || "").trim() : "";
      const rawPrice = priceIdx >= 0 ? (cells[priceIdx] || "").trim() : "";
      const rawGender = genderIdx >= 0 ? (cells[genderIdx] || "").trim() : "";
      const rawMode = modeIdx >= 0 ? (cells[modeIdx] || "").trim() : "";
      const rawFilterCategories = filterCatIdx >= 0 ? (cells[filterCatIdx] || "").trim() : "";
      const rawCollectionCategory = collectionCatIdx >= 0 ? (cells[collectionCatIdx] || "").trim() : "";
      const rawDescription = descIdx >= 0 ? (cells[descIdx] || "").trim() : "";

      const rowErrors = [];

      // Validate Title
      if (!rawTitle) {
        rowErrors.push("Title is required");
      } else if (rawTitle.length < 3) {
        rowErrors.push("Title must be at least 3 characters");
      } else if (rawTitle.length > 100) {
        rowErrors.push("Title can be at most 100 characters");
      } else if (!TITLE_ALLOWED_REGEX.test(rawTitle)) {
        rowErrors.push("Title can only contain letters, numbers, spaces, -, _, and ()");
      } else if (!CONTAINS_LETTER_REGEX.test(rawTitle)) {
        rowErrors.push("Title must contain at least one letter");
      }

      // Validate SKU ID
      if (!rawSku) {
        rowErrors.push("SKU ID is required");
      } else if (rawSku.length < 3) {
        rowErrors.push("SKU ID must be at least 3 characters");
      } else if (rawSku.length > 20) {
        rowErrors.push("SKU ID can be at most 20 characters");
      } else if (!SKU_ALLOWED_REGEX.test(rawSku)) {
        rowErrors.push("SKU ID can only contain letters, numbers, -, and _");
      } else {
        const normalizedSku = rawSku.toLowerCase();
        if (seenSkus.has(normalizedSku)) {
          rowErrors.push(`Duplicate SKU "${rawSku}" (already defined on row ${seenSkus.get(normalizedSku)})`);
        } else {
          seenSkus.set(normalizedSku, rowNum);
        }
      }

      // Validate Price
      if (!rawPrice) {
        rowErrors.push("Price is required");
      } else if (!PRICE_REGEX.test(rawPrice) || Number(rawPrice) <= 0) {
        rowErrors.push("Price must be a positive number with up to 2 decimal places");
      } else if (Number(rawPrice) > 9999999) {
        rowErrors.push("Price cannot exceed 99,99,999");
      }

      // Validate Gender
      const normalizedGender = normalizeGender(rawGender);
      if (!normalizedGender) {
        rowErrors.push("Gender is required (women / men)");
      }

      // Validate Mode
      const normalizedMode = normalizeMode(rawMode);
      if (!normalizedMode) {
        rowErrors.push("Mode is required (model_to_model / mannequin_to_model)");
      }

      // Validate Categories
      if (!rawFilterCategories) {
        rowErrors.push("Filter category is required");
      }
      if (!rawCollectionCategory) {
        rowErrors.push("Collection category is required");
      }

      if (rowErrors.length > 0) {
        errorsList.push({ row: rowNum, sku: rawSku || "N/A", errors: rowErrors });
      } else {
        validData.push({
          sku: rawSku,
          title: rawTitle,
          price: Number(rawPrice),
          gender: normalizedGender,
          mode: normalizedMode,
          filterCategories: rawFilterCategories,
          collectionCategory: rawCollectionCategory,
          description: rawDescription,
        });
      }
    }

    return { validData, errors: errorsList };
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "csv") {
      toast.error("Invalid file format. Please upload a .csv file.");
      return;
    }

    if (file.size > MAX_CSV_FILE_SIZE) {
      toast.error(
        `File size exceeds 80MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
      );
      return;
    }

    setSelectedFile(file);
    setValidationErrors([]);
    setParsedData([]);
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const { validData, errors } = parseAndValidateCSV(text);

      setValidationErrors(errors);
      setParsedData(validData);

      if (errors.length > 0) {
        toast.error(`Found validation errors in ${errors.length} row(s)`);
      } else {
        toast.success(`CSV parsed successfully! Ready to upload ${validData.length} product(s).`);
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!selectedFile) {
      toast.error("Please select a CSV file to upload.");
      return;
    }

    if (validationErrors.length > 0) {
      toast.error("Please fix all CSV validation errors before uploading.");
      return;
    }

    if (parsedData.length === 0) {
      toast.error("No valid product rows found in CSV file.");
      return;
    }

    setIsUploading(true);

    try {
      if (onSubmit) {
        await onSubmit(parsedData);
      } else {
        const token = sessionStorage.getItem("accessToken");
        const url = `${endpoint.API_BASE_URL}${endpoint.BULK_UPLOAD_PRODUCTS}`;

        const response = await axios.post(url, parsedData, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const resData = response.data;
        const failedProducts = resData?.data?.failedProducts || [];
        const failedCount = resData?.data?.failedCount ?? failedProducts.length;
        const succeededCount = resData?.data?.succeededCount ?? resData?.data?.successfulProducts?.length ?? 0;

        if (failedCount > 0 || resData?.status === "error" || resData?.success === false) {
          setUploadResult(resData);
          toast.error(resData?.message || `Bulk upload completed with ${failedCount} error(s).`);
          if (succeededCount > 0 && onRefreshProducts) {
            onRefreshProducts();
          }
        } else if (resData?.success || resData?.status === "success") {
          toast.success(resData?.message || "Bulk products uploaded successfully!");
          if (onRefreshProducts) onRefreshProducts();
          resetState();
          onClose();
        } else {
          setUploadResult(resData);
          toast.error(resData?.message || "Failed to upload bulk products.");
        }
      }
    } catch (err) {
      const resData = err.response?.data;
      const failedProducts = resData?.data?.failedProducts || [];
      const failedCount = resData?.data?.failedCount ?? (failedProducts.length > 0 ? failedProducts.length : parsedData.length);
      const succeededCount = resData?.data?.succeededCount ?? 0;

      if (resData && (failedProducts.length > 0 || resData.message || resData.data)) {
        setUploadResult(resData);
        toast.error(resData.message || "Bulk upload completed with errors.");
        if (succeededCount > 0 && onRefreshProducts) {
          onRefreshProducts();
        }
      } else {
        const serverMessage = err.response?.data?.message || err.message || "Failed to upload bulk products.";
        setUploadResult({
          status: "error",
          message: serverMessage,
          data: {
            total: parsedData.length,
            succeededCount: 0,
            failedCount: parsedData.length,
            failedProducts: [
              {
                row: 1,
                sku: "-",
                title: "-",
                error: serverMessage,
                details: serverMessage,
              },
            ],
          },
        });
        toast.error(serverMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    resetState();
    onClose();
  };

  const clearUploadResult = () => {
    setUploadResult(null);
    setSelectedFile(null);
    setParsedData([]);
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    selectedFile,
    isDragOver,
    parsedData,
    validationErrors,
    uploadResult,
    isUploading,
    fileInputRef,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleRemoveFile,
    handleFormSubmit,
    handleModalClose,
    clearUploadResult,
  };
};
